import React, { useEffect } from 'react';
import { Form, Input, Select, Checkbox, Row, Col } from 'antd';
import ConditionalFormItem from '../../ConditionalFormItem';
import { SERVICE_TYPE_MAP } from '../../../config/service-type-map';
import NATION_OPTIONS from "../../../config/nations";
import feeService from '../../../services/fee';

const { Option } = Select;

interface CompanyBusinessTabProps {
  // 由外层传递 serviceType，本组件内部从 SERVICE_TYPE_MAP 计算选项
  serviceType: string;
  getReadOnlyProps?: (fieldType?: 'input' | 'select') => any;
  baseCountryMode?: boolean;
}

// 公司业务信息 Tab：从 DynamicOrderForm 中的内联 CompanyInfo 提取
const CompanySection: React.FC<CompanyBusinessTabProps> = ({ serviceType, getReadOnlyProps = () => ({}), baseCountryMode = false }) => {
  const serviceTypeOptions = SERVICE_TYPE_MAP[serviceType] || {};
  const formInstance = Form.useFormInstance();
  const watchedNation = Form.useWatch('nation', formInstance);

  useEffect(() => {
    const loadChannels = async () => {
      try {
        if (!watchedNation) {
          return;
        }
        await feeService.getSubmitChannelsByNation(watchedNation as string);
      } catch (e) {
        // ignore
      }
    };
    loadChannels();
  }, [watchedNation]);

  return (
    <>
      {/* 基本信息（复制自 BasicInfoTab，去除申请人相关） */}
      {/* 子业务名称 */}
      <Form.Item label="子业务名称" name="sub_service" rules={[{ required: true, message: '请选择子业务名称' }]}>
        <Select placeholder="请选择具体的子业务名称">
          {(Object.entries(serviceTypeOptions) as [string, string][]).map(([key, value]) => (
            <Option key={key} value={key}>
              {value}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={baseCountryMode ? '基础国家/地区' : '国家/地区'}
        name="nation"
        rules={[{ required: true, message: baseCountryMode ? '请选择基础国家/地区' : '请选择国家/地区' }]}
      >
        <Select
          placeholder={baseCountryMode ? '请选择基础国家/地区' : '请选择国家/地区'}
          showSearch
          filterOption={(input, option) =>
            String(option?.children || '').toLowerCase().includes(input.toLowerCase())
          }
          data-testid="order-nation"
        >
          {NATION_OPTIONS.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item label="订单编号" name="order_no" rules={[{ required: true, message: '订单编号自动生成' }]}>
            <Input placeholder="自动生成" readOnly style={{ backgroundColor: '#f5f5f5', color: '#999' }} data-testid="order-no" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="公司名称" name="company_name" rules={[{ required: true, message: '请输入公司名称' }]}>
        <Input placeholder="请输入公司名称" />
      </Form.Item>

      <ConditionalFormItem condition="sub_service = registration || sub_service = annual_review || sub_service = audit">
        <Form.Item  label="公司地址" name="company_address">
          <Input placeholder="请输入公司地址" />
        </Form.Item>
      </ConditionalFormItem>

      <Form.Item label="公司编号" name="company_no" rules={[{ required: true, message: '请输入公司编号' }]}>
        <Input placeholder="请输入公司编号" />
      </Form.Item>

      <ConditionalFormItem condition="sub_service = registration || sub_service = annual_review || sub_service = audit">
        <Form.Item label="公司类型" name="company_type">
          <Input placeholder="请输入公司类型" />
        </Form.Item>
        <Form.Item label="公司代表" name="legal_representative">
          <Input placeholder="请输入公司代表" />
        </Form.Item>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service = registration">
        <Form.Item label="是否含税号" name="include_vat_no" valuePropName="checked">
          <Checkbox>含税号</Checkbox>
        </Form.Item>
        <Form.Item label="是否含开户" name="include_bank_account" valuePropName="checked">
          <Checkbox>含开户</Checkbox>
        </Form.Item>
        <Form.Item label="是否需要外籍董事" name="include_foreign_directors" valuePropName="checked">
          <Checkbox>需要外籍董事</Checkbox>
        </Form.Item>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service = annual_review || sub_service = audit">
        <Form.Item label="年审次数" name="annual_inspection_times" rules={[{ required: true, message: '请输入年审次数' }]}>
          <Input placeholder="请输入年审次数" />
        </Form.Item>
      </ConditionalFormItem>
      <ConditionalFormItem condition="sub_service = bank_account_opening">
        <Form.Item label="开户银行" name="account_bank" rules={[{ required: true, message: '请输入开户银行' }]}>
          <Input placeholder="请输入开户银行" />
        </Form.Item>
      </ConditionalFormItem>
      <ConditionalFormItem condition="sub_service = cancellation">
        <Form.Item label="注销原因" name="cancellation_reason" rules={[{ required: true, message: '请输入注销原因' }]}>
          <Input placeholder="请输入注销原因" />
        </Form.Item>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service = information_changes">
        <Form.Item label="变更项目" name="change_items">
          <Select mode="multiple" placeholder="请选择变更项目">
            <Option value="公司名称">公司名称</Option>
            <Option value="公司地址">公司地址</Option>
            <Option value="公司成员信息">公司成员信息</Option>
            <Option value="变更秘书服务">变更秘书服务</Option>
          </Select>
        </Form.Item>
      </ConditionalFormItem>

      <Form.Item label="备注" name="remark">
        <Input.TextArea rows={3} placeholder="请输入备注信息" data-testid="order-remark" />
      </Form.Item>
    </>
  );
};

export default CompanySection;
